import { useEffect, useState, useCallback } from "react";
import { Plus, Gift, Ticket } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const empty = { amount: "", recipient_name: "", recipient_email: "", message: "" };

export default function GiftCards() {
  const [cards, setCards] = useState(null);
  const [open, setOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [redeem, setRedeem] = useState({ code: "", amount: "" });

  const load = useCallback(() => api.get("/gift-cards").then((r) => setCards(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const issue = async (e) => {
    e.preventDefault();
    try {
      await api.post("/gift-cards", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Gift card issued");
      setForm(empty); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const doRedeem = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/gift-cards/redeem", { code: redeem.code, amount: parseFloat(redeem.amount) || 0 });
      toast.success(`Redeemed $${data.redeemed.toFixed(2)} · Balance $${data.balance.toFixed(2)}`);
      setRedeem({ code: "", amount: "" }); setRedeemOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  if (!cards) return <Loading />;
  const outstanding = cards.filter((c) => c.status === "active").reduce((s, c) => s + c.balance, 0);

  return (
    <div data-testid="giftcards-page">
      <PageHeader title="Digital Gift Cards" subtitle="Issue and redeem gift cards. Guests can also buy them from your QR menu."
        action={<div className="flex gap-2">
          <Button variant="outline" data-testid="redeem-gift-btn" onClick={() => setRedeemOpen(true)} className="border-border hover:bg-muted"><Ticket className="h-4 w-4 mr-1" /> Redeem</Button>
          <Button data-testid="issue-gift-btn" onClick={() => setOpen(true)} className="bg-primary hover:bg-[#B35A42] text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Issue card</Button>
        </div>} />

      <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-4 mb-6 text-sm">
        Outstanding balance across active cards: <span className="font-semibold">${outstanding.toFixed(2)}</span>
      </div>

      {cards.length === 0 ? (
        <EmptyState icon={Gift} title="No gift cards yet" desc="Issue your first digital gift card or let guests buy them from your QR menu."
          action={<Button onClick={() => setOpen(true)} className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Issue card</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c) => (
            <div key={c.id} data-testid={`giftcard-${c.id}`} className="rounded-xl p-5 bg-gradient-to-br from-foreground to-[#2c2c2c] text-[#FDFBF7]">
              <div className="flex items-center justify-between">
                <Gift className="h-6 w-6 text-accent" />
                <Badge className={c.status === "active" ? "bg-secondary text-secondary-foreground border-0" : "bg-white/20 text-white border-0"}>
                  {c.status}
                </Badge>
              </div>
              <p className="mt-5 font-mono text-lg tracking-wider">{c.code}</p>
              <div className="flex items-end justify-between mt-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[#FDFBF7]/60">Balance</p>
                  <p className="font-heading text-3xl font-semibold">${Number(c.balance).toFixed(2)}</p>
                </div>
                <p className="text-sm text-[#FDFBF7]/60">of ${Number(c.amount).toFixed(2)}</p>
              </div>
              {c.recipient_name && <p className="mt-3 text-sm text-[#FDFBF7]/70">For {c.recipient_name}</p>}
              <p className="text-xs text-[#FDFBF7]/40 mt-1 capitalize">{c.source}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Issue gift card</DialogTitle></DialogHeader>
          <form onSubmit={issue} className="space-y-4">
            <div className="space-y-1.5"><Label>Amount ($)</Label>
              <Input data-testid="gift-form-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Recipient name</Label>
                <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Recipient email</Label>
                <Input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Message</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
            <DialogFooter>
              <Button type="submit" data-testid="gift-form-submit" className="bg-primary hover:bg-[#B35A42] text-primary-foreground">Issue card</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={redeemOpen} onOpenChange={setRedeemOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Redeem gift card</DialogTitle></DialogHeader>
          <form onSubmit={doRedeem} className="space-y-4">
            <div className="space-y-1.5"><Label>Gift card code</Label>
              <Input data-testid="redeem-code" required value={redeem.code} onChange={(e) => setRedeem({ ...redeem, code: e.target.value })} placeholder="GIFT-XXXX-XXXX" /></div>
            <div className="space-y-1.5"><Label>Amount to redeem ($)</Label>
              <Input data-testid="redeem-amount" type="number" step="0.01" required value={redeem.amount} onChange={(e) => setRedeem({ ...redeem, amount: e.target.value })} /></div>
            <DialogFooter>
              <Button type="submit" data-testid="redeem-submit" className="bg-secondary hover:bg-[#4B5845] text-secondary-foreground">Redeem</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
